import { useQuery } from "@tanstack/react-query";
import { devicesApi } from "@/api";
import { Smartphone } from "lucide-react";
import type { DeviceStatusType } from "@/types";

const statusConfig: Record<
  DeviceStatusType,
  { color: string; bg: string; label: string }
> = {
  ONLINE: { color: "bg-green-500", bg: "bg-green-50", label: "オンライン" },
  OFFLINE: { color: "bg-slate-400", bg: "bg-slate-50", label: "オフライン" },
  ERROR: { color: "bg-red-500", bg: "bg-red-50", label: "エラー" },
};

function timeAgo(dateStr: string | null): string {
  if (!dateStr) return "不明";
  const diff = Date.now() - new Date(dateStr).getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return "たった今";
  if (min < 60) return `${min}分前`;
  const hours = Math.floor(min / 60);
  if (hours < 24) return `${hours}時間前`;
  return `${Math.floor(hours / 24)}日前`;
}

export default function Devices() {
  const { data, isLoading } = useQuery({
    queryKey: ["devices"],
    queryFn: () => devicesApi.list(),
    refetchInterval: 30000,
  });

  if (isLoading) return <p className="text-slate-500">読み込み中...</p>;

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-bold">端末ステータス</h2>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {data?.data.map((device) => {
          const config = statusConfig[device.status];
          return (
            <div
              key={device.id}
              className={`${config.bg} p-4 rounded-lg border space-y-3`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Smartphone size={20} className="text-slate-600" />
                  <span className="font-medium">{device.name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`w-2.5 h-2.5 rounded-full ${config.color}`} />
                  <span className="text-sm">{config.label}</span>
                </div>
              </div>

              <div className="text-sm space-y-1 text-slate-600">
                <p>電話番号: <span className="font-mono">{device.phoneNumber}</span></p>
                <p>最終応答: {timeAgo(device.lastSeenAt)}</p>
                <div className="flex items-center gap-2">
                  <span>本日送信:</span>
                  <div className="flex-1 bg-slate-200 rounded-full h-2">
                    <div
                      className="bg-blue-500 h-2 rounded-full"
                      style={{
                        width: `${Math.min((device.dailySentCount / 500) * 100, 100)}%`,
                      }}
                    />
                  </div>
                  <span className="font-mono text-xs">
                    {device.dailySentCount}/500
                  </span>
                </div>
              </div>
            </div>
          );
        })}

        {!data?.data.length && (
          <p className="text-slate-400 col-span-full">
            登録済みの端末がありません
          </p>
        )}
      </div>
    </div>
  );
}
